(function () {
  window.swapwearCompositePreview = function (paired = false) {
    let roots = [], observer, sizeObserver, status, frame = 0, stopped = false, revision = 0;
    const decoded = new WeakMap(), decoding = new WeakMap(), decodeWaiters = new Set();
    let loadTimer = 0, waitingFor = '', timedOut = false;
    const resetWait = () => { clearTimeout(loadTimer); loadTimer = 0; timedOut = false; };
    const decodeImage = image => {
      const token = source(image), existing = decoding.get(image);
      if (existing?.token === token) return existing.promise;
      // decode() is an optimization, not a second unbounded loading gate. Both
      // images must still be complete before the pair can be painted together.
      const promise = new Promise(resolve => {
        let timer;
        const finish = () => { clearTimeout(timer); decodeWaiters.delete(finish); resolve(); };
        decodeWaiters.add(finish);
        timer = setTimeout(finish, 1000);
        try { Promise.resolve(image.decode?.()).then(finish, finish); } catch (_) { finish(); }
      });
      decoding.set(image, { token, promise });
      return promise;
    };
    const source = image => image.getAttribute('src') ? image.getAttribute('src') + '|' + (image.currentSrc || '') : '';
    const images = () => roots.flatMap(root => Array.from(root.querySelectorAll('img')))
      .filter(image => image.getClientRects().length && !image.closest('[x-cloak]'));
    const pending = value => roots.forEach(root => root.toggleAttribute('data-composite-pending', value));
    const schedule = () => {
      if (stopped || frame) return;
      // Hide both layers before the next paint after a source/selection change.
      if (images().some(image => !image.complete || !image.naturalWidth || decoded.get(image) !== source(image))) pending(true);
      frame = requestAnimationFrame(check);
    };
    const retry = event => {
      event.preventDefault(); event.stopPropagation();
      resetWait();
      images().filter(image => (!image.complete || !image.naturalWidth) && image.getAttribute('src')).forEach(image => {
        const url = new URL(image.currentSrc || image.getAttribute('src'), location.href);
        url.searchParams.set('sw_image_retry', Date.now());
        image.removeAttribute('srcset');
        image.src = url.href;
      });
      schedule();
    };
    const check = async () => {
      frame = 0;
      const current = ++revision, active = images();
      const key = active.map(image => image.getAttribute('src') + '|' + (image.getAttribute('srcset') || '')).join('\n');
      if (key !== waitingFor) { resetWait(); waitingFor = key; }
      const failed = active.some(image => source(image) && image.complete && !image.naturalWidth);
      const loaded = active.length > 0 && active.every(image => source(image) && image.complete && image.naturalWidth > 0);
      if (loaded || !active.length || failed) resetWait();
      else if (!loadTimer && !timedOut) loadTimer = setTimeout(() => {
        loadTimer = 0; timedOut = true; schedule();
      }, 15000);
      const needsRetry = failed || timedOut;
      pending(!loaded);
      status.hidden = loaded;
      status.classList.toggle('sw-composite-status--error', needsRetry);
      const text = needsRetry ? status.dataset.retry : '';
      if (status.textContent !== text) status.textContent = text;
      status.setAttribute('aria-label', needsRetry ? status.dataset.retry : status.dataset.loading);
      status.setAttribute('role', needsRetry ? 'button' : 'status');
      status.tabIndex = needsRetry ? 0 : -1;
      if (!loaded) return;
      const sources = active.map(source);
      const undecoded = active.filter(image => decoded.get(image) !== source(image));
      if (undecoded.length) {
        pending(true); status.hidden = false;
        await Promise.all(undecoded.map(decodeImage));
      }
      if (stopped || current !== revision || active.some((image, index) => source(image) !== sources[index] || !image.complete || !image.naturalWidth)) return;
      active.forEach(image => {
        decoded.set(image, source(image));
        if (image.classList?.contains('js-image')) {
          image.classList.add('loaded');
          image.closest('.js-picture')?.classList.add('loaded');
        }
      });
      pending(false); status.hidden = true;
    };
    return {
      init() {
        const root = this.$el;
        roots = paired && root.previousElementSibling?.hasAttribute('data-composite-panel')
          ? [root.previousElementSibling, root] : [root];
        status = document.createElement('span');
        status.className = 'sw-composite-status';
        status.dataset.loading = root.dataset.previewLoading || 'Loading preview…';
        status.dataset.retry = root.dataset.previewRetry || 'That didn’t quite work. Tap to try again.';
        status.addEventListener('click', event => { if (status.classList.contains('sw-composite-status--error')) retry(event); });
        status.addEventListener('keydown', event => { if (status.classList.contains('sw-composite-status--error') && ['Enter', ' '].includes(event.key)) retry(event); });
        root.appendChild(status);
        observer = new MutationObserver(schedule);
        if (typeof ResizeObserver !== 'undefined') sizeObserver = new ResizeObserver(schedule);
        roots.forEach(node => {
          node.classList.add('sw-composite-layer');
          node.addEventListener('load', schedule, true);
          node.addEventListener('error', schedule, true);
          observer.observe(node, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'style', 'x-cloak'] });
          sizeObserver?.observe(node);
        });
        schedule();
      },
      destroy() {
        stopped = true; revision++; resetWait();
        decodeWaiters.forEach(finish => finish());
        cancelAnimationFrame(frame); observer?.disconnect(); sizeObserver?.disconnect();
        roots.forEach(node => {
          node.removeEventListener('load', schedule, true);
          node.removeEventListener('error', schedule, true);
          node.removeAttribute('data-composite-pending');
        });
        status?.remove(); roots = [];
      }
    };
  };
})();
